import { token } from '../theme/tokens.js';

export interface CheckboxProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /**
   * The accessible name, and the visible text when `showLabel` is set. This
   * component owns no strings — a shared component that hardcodes English is
   * not shared.
   */
  label: string;
  /** Render `label` beside the box as well as on it. Off by default. */
  showLabel?: boolean;
  /**
   * A change is in flight. The control is held at its current position and
   * cannot be pressed again. Separate from `disabled` on `Switch`'s own
   * reasoning: busy is "this is happening", disabled is "this is not
   * available to you".
   */
  busy?: boolean;
  disabled?: boolean;
  /**
   * Forwarded as the button's own `id`, so a caller's separate `<label
   * htmlFor>` (a layout that puts the visible text somewhere other than
   * `showLabel`'s position) still activates this control — a button is a
   * labelable element, so that association works the same way it would for a
   * native `<input>`.
   */
  id?: string;
  /** Forwarded as `aria-describedby`, for a caller with hint text beside the box. */
  describedBy?: string;
}

/**
 * A checkbox. `role="checkbox"` with `aria-checked` — `Switch`'s shape
 * (a `<button>`, so Space and Enter work for free, and the caller is told
 * which state was asked for rather than that a press happened), for a field
 * that is on or off rather than a live setting.
 *
 * **Never a filled box.** The prior styled checkbox in this app (`FolderView`'s
 * `ToggleBox`) filled with `bp-blue` when checked and drew its glyph in
 * `currentColor`, inherited from surrounding body text rather than set
 * explicitly — against the blue fill that read at roughly 1.3:1 in the
 * command room, well under WCAG's 3:1 floor for a non-text control. This
 * component draws the same bordered box in both states and never fills it:
 * off is an empty box at `bp-text-faint` (this system's 3:1 non-text token);
 * on is the same box outlined in `bp-blue` with a `bp-blue` checkmark inside
 * it, so the mark's colour is never left to inheritance.
 */
export function Checkbox({
  checked,
  onChange,
  label,
  showLabel = false,
  busy = false,
  disabled = false,
  id,
  describedBy,
}: CheckboxProps) {
  const inert = busy || disabled;

  return (
    <span className="inline-flex items-center gap-2">
      <button
        id={id}
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={label}
        aria-describedby={describedBy}
        aria-busy={busy}
        disabled={inert}
        onClick={() => {
          if (!inert) onChange(!checked);
        }}
        className="flex size-4 shrink-0 items-center justify-center border disabled:cursor-not-allowed disabled:opacity-60"
        style={{
          borderColor: checked ? token('bp-blue') : token('bp-text-faint'),
          borderRadius: token('radius-sm'),
        }}
      >
        {checked ? <CheckGlyph /> : null}
      </button>
      {showLabel ? (
        <span className="text-sm" style={{ color: token('bp-text-secondary') }}>
          {label}
        </span>
      ) : null}
    </span>
  );
}

/** The checked glyph. `bp-blue` stroke, set explicitly rather than inherited. */
function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={12}
      height={12}
      fill="none"
      stroke={token('bp-blue')}
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
